import { tokens } from '@/lib/tokens.js';
import { useState } from 'react';
import { useDeprecateTemplate } from '../../hooks/use-admin-templates.js';

const styles = {
  overlay: {
    position: 'fixed' as const,
    inset: 0,
    backgroundColor: 'rgba(0,0,0,0.7)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 50,
  },
  dialog: {
    backgroundColor: tokens.colors.bgCardDark,
    borderRadius: tokens.radius.card,
    padding: '1.5rem',
    width: '420px',
    maxWidth: '90vw',
    boxShadow: '0 20px 60px rgba(0,0,0,0.5)',
  },
  title: {
    fontSize: '1.125rem',
    fontWeight: 700,
    color: tokens.colors.textDark,
    marginBottom: '1rem',
  },
  label: {
    display: 'block',
    fontSize: '0.875rem',
    color: tokens.colors.textMutedDark,
    marginBottom: '0.5rem',
  },
  input: {
    width: '100%',
    backgroundColor: '#0f172a',
    border: `1px solid ${tokens.colors.borderDark}`,
    borderRadius: tokens.radius.input,
    color: tokens.colors.textDark,
    padding: '0.5rem 0.75rem',
    fontSize: '0.875rem',
    outline: 'none',
    boxSizing: 'border-box' as const,
    fontFamily: 'inherit',
    marginBottom: '1.25rem',
  },
  actions: {
    display: 'flex',
    justifyContent: 'flex-end',
    gap: '0.75rem',
  },
  btnDanger: {
    backgroundColor: tokens.colors.error,
    color: '#fff',
    border: 'none',
    borderRadius: tokens.radius.button,
    padding: '0.5rem 1rem',
    fontSize: '0.875rem',
    cursor: 'pointer',
    fontWeight: 500,
  },
  btnCancel: {
    backgroundColor: 'transparent',
    color: tokens.colors.textDark,
    border: `1px solid ${tokens.colors.borderDark}`,
    borderRadius: tokens.radius.button,
    padding: '0.5rem 1rem',
    fontSize: '0.875rem',
    cursor: 'pointer',
  },
};

interface TemplateDeprecateDialogProps {
  templateId: string;
  templateCode: string;
  onClose: () => void;
  onSuccess?: () => void;
}

export function TemplateDeprecateDialog({
  templateId,
  templateCode,
  onClose,
  onSuccess,
}: TemplateDeprecateDialogProps) {
  const [reason, setReason] = useState('');
  const deprecate = useDeprecateTemplate();

  function handleConfirm() {
    deprecate.mutate(
      { templateId, body: reason ? { reason } : {} },
      {
        onSuccess: () => {
          onSuccess?.();
          onClose();
        },
      },
    );
  }

  return (
    <div style={styles.overlay}>
      <dialog open style={styles.dialog} aria-label="Deprecate template">
        <h2 style={styles.title}>Deprecate template</h2>
        <p
          style={{ fontSize: '0.875rem', color: tokens.colors.textMutedDark, marginBottom: '1rem' }}
        >
          You are about to deprecate{' '}
          <strong style={{ color: tokens.colors.textDark, fontFamily: 'monospace' }}>
            {templateCode}
          </strong>
          . This action cannot be undone.
        </p>
        <label style={styles.label} htmlFor="deprecate-reason">
          Reason (optional)
        </label>
        <input
          id="deprecate-reason"
          style={styles.input}
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          placeholder="Reason for deprecation…"
          aria-label="Deprecation reason"
        />
        {deprecate.isError && (
          <p
            style={{ color: tokens.colors.error, fontSize: '0.875rem', marginBottom: '0.75rem' }}
            role="alert"
          >
            Error deprecating template.
          </p>
        )}
        <div style={styles.actions}>
          <button type="button" style={styles.btnCancel} onClick={onClose} aria-label="Cancel">
            Cancel
          </button>
          <button
            type="button"
            style={{ ...styles.btnDanger, opacity: deprecate.isPending ? 0.7 : 1 }}
            onClick={handleConfirm}
            disabled={deprecate.isPending}
            aria-label="Confirm deprecation"
          >
            {deprecate.isPending ? 'Deprecating…' : 'Deprecate'}
          </button>
        </div>
      </dialog>
    </div>
  );
}
